/**
 * Auth synchronization utility for cross-subdomain authentication
 * Syncs authentication state between landing page (youpass.me) and admin (admin.youpass.me)
 */

const AUTH_SYNC_KEY = 'youpass-auth-sync';
const AUTH_SYNC_EVENT = 'youpass-auth-change';

interface AuthSyncMessage {
  type: 'login' | 'logout' | 'check';
  timestamp: number;
}

/**
 * Broadcast auth state change to other windows/tabs
 */
export function broadcastAuthChange(type: 'login' | 'logout') {
  if (typeof window === 'undefined') return;

  const message: AuthSyncMessage = {
    type,
    timestamp: Date.now(),
  };

  // Save to sessionStorage as a flag
  try {
    sessionStorage.setItem(AUTH_SYNC_KEY, JSON.stringify(message));
  } catch (e) {
    console.warn('Could not save auth sync flag:', e);
  }

  // Broadcast to other windows/tabs via postMessage
  window.postMessage(
    {
      type: AUTH_SYNC_EVENT,
      data: message,
    },
    '*'
  );
}

/**
 * Get the other domain (admin if on landing, landing if on admin)
 */
export function getOtherDomain(): string | null {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  
  if (hostname.includes('admin')) {
    // We're on admin, return landing page URL
    return import.meta.env.VITE_FRONTEND_URL || 
           (hostname.includes('staging') 
             ? 'https://staging.youpass.me' 
             : 'https://youpass.me');
  } else {
    // We're on landing, return admin URL
    return import.meta.env.VITE_PWA_URL || 
           (hostname.includes('staging') 
             ? 'https://admin-staging.youpass.me' 
             : 'https://admin.youpass.me');
  }
}

/**
 * Listen for auth changes from other windows/tabs
 */
export function listenForAuthChanges(callback: (type: 'login' | 'logout') => void) {
  if (typeof window === 'undefined') return () => {};

  const handleMessage = (event: MessageEvent) => {
    // Accept messages from any origin (both apps are trusted)
    if (event.data?.type === AUTH_SYNC_EVENT) {
      const message = event.data.data as AuthSyncMessage;
      if (message.type === 'login' || message.type === 'logout') {
        callback(message.type);
      }
    }
  };

  // Listen for storage events (same origin only, but useful for same app in multiple tabs)
  const handleStorage = (event: StorageEvent) => {
    if (event.key === AUTH_SYNC_KEY && event.newValue) {
      try {
        const message = JSON.parse(event.newValue) as AuthSyncMessage;
        if (message.type === 'login' || message.type === 'logout') {
          callback(message.type);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  };

  window.addEventListener('message', handleMessage);
  window.addEventListener('storage', handleStorage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
    window.removeEventListener('storage', handleStorage);
  };
}
